import DefaultImage from '@/assets/icons/default-avatar-icon-of-social-media-user-vector.jpg';
import IconStar from '@/assets/icons/product/icon-star-comment.svg';
import {
  OrderDirection,
  ProductQuery,
  ReviewSortField,
  ReviewSortingInput,
  useReviewsQuery,
} from '@/graphql';
import { mapEdgesToItems } from '@/helpers/maps';
import { ReviewsText } from '@/helpers/reviewsText';
import useUserStore from '@/zustand/useUser';
import type { NextPage } from 'next';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { FaRegUser } from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import { Button, Pagination } from 'rsuite';
import styles from './comments.module.scss';
import UserReviewModal from './userReviewModal';

interface RatingProps {
  data: ProductQuery | undefined;
}

interface ReviewsData {
  1: number;
  2: number;
  3: number;
  4: number;
  5: number;
}

const REVIEWS_PER_PAGE = 2;

const Rating: NextPage<RatingProps> = ({ data }) => {
  const reviewsData: ReviewsData | undefined = data?.product?.reviewsData;
  const { user } = useUserStore();
  const totalReviews =
    (reviewsData?.[1] ?? 0) +
      (reviewsData?.[2] ?? 0) +
      (reviewsData?.[3] ?? 0) +
      (reviewsData?.[4] ?? 0) +
      (reviewsData?.[5] ?? 0) || 0;
  const [open, setOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<ReviewSortingInput | null>({
    field: ReviewSortField.RATING,
    direction: OrderDirection.DESC,
  });
  const [activeSort, setActiveSort] = useState<'best' | 'worst' | 'new'>(
    'best'
  );
  const [showSkeletons, setShowSkeletons] = useState(false);
  const t = useTranslations('Components.product.comments');
  const locale = useLocale();
  const {
    data: reviews,
    loading,
    fetchMore,
    updateQuery,
  } = useReviewsQuery({
    variables: {
      first: REVIEWS_PER_PAGE,
      filter: {
        product: data?.product?.id,
      },
      sortBy: sortBy,
    },
    // notifyOnNetworkStatusChange: true,
  });

  const reviewsListData = mapEdgesToItems(reviews?.reviews);

  const handleSort = (
    sortType: ReviewSortingInput | null,
    sortKey: 'best' | 'worst' | 'new'
  ) => {
    setSortBy(sortType);
    setCurrentPage(1);
    setActiveSort(sortKey);

    fetchMore({
      variables: {
        first: REVIEWS_PER_PAGE,
        filter: {
          product: data?.product?.id,
        },
        sortBy: sortType,
      },
    });
  };

  const totalCount = reviews?.reviews?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / REVIEWS_PER_PAGE);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    const cursorDirection = page > currentPage ? 'after' : 'before';
    const cursor =
      cursorDirection === 'after'
        ? reviews?.reviews?.pageInfo?.endCursor
        : reviews?.reviews?.pageInfo?.startCursor;

    fetchMore({
      variables: {
        [cursorDirection]: cursor,
        first: cursorDirection === 'after' ? REVIEWS_PER_PAGE : undefined,
        last: cursorDirection === 'before' ? REVIEWS_PER_PAGE : undefined,
        filter: {
          product: data?.product?.id,
        },
        sortBy: sortBy,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.reviews) {
          return prev;
        }

        return {
          __typename: 'Query',
          reviews: {
            __typename: 'ReviewCountableConnection',
            totalCount: fetchMoreResult.reviews.totalCount ?? 0,
            edges: fetchMoreResult.reviews.edges,
            pageInfo: fetchMoreResult.reviews.pageInfo,
          },
        };
      },
    });
  };

  const calculatePercentage = (stars: keyof ReviewsData): string => {
    if (!reviewsData || totalReviews === 0) {
      return '0%';
    }
    const percentage = (reviewsData[stars] / totalReviews) * 100;
    return percentage.toFixed(0) + '%';
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const starRatings = reviewsData
    ? (Object.entries(reviewsData) as unknown as [keyof ReviewsData, number][])
    : [];

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (loading) {
      setShowSkeletons(true);
    } else {
      timer = setTimeout(() => {
        setShowSkeletons(false);
      }, 500);
    }

    return () => clearTimeout(timer);
  }, [loading]);

  return (
    <div className={styles.content}>
      <div className={styles.rating}>
        <div className={styles.frameParent}>
          <div className={styles.starParent}>
            <IconStar />
            <div className={styles.div}>{data?.product?.rating}</div>
          </div>
          <div className={styles.div}>
            {data?.product?.activeReviewsCount != null ? (
              <ReviewsText
                count={data.product.activeReviewsCount}
                locale={locale}
              />
            ) : (
              t('no-comment')
            )}
          </div>
        </div>
        <div className={styles.div2}>
          {starRatings.map(([star, count]) => (
            <div className={styles.star} key={star}>
              <div className={styles.div3}>{star}</div>
              <div className={styles.elem}>
                <div
                  className={count > 0 ? styles.elem1 : styles.elem5}
                  style={{ width: calculatePercentage(star) }}
                />
              </div>
              <div className={styles.div4}>{calculatePercentage(star)}</div>
            </div>
          ))}
        </div>
        {user && (
          <div className={styles.button}>
            <Button
              type="button"
              className={styles.button1}
              onClick={() => setOpen(true)}
            >
              {t('type-comment')}
            </Button>
            <div className={styles.div13}>{t('rule-type-comment')}</div>
          </div>
        )}
      </div>
      <div className={styles.right}>
        {data?.product?.activeReviewsCount != null &&
          data.product.activeReviewsCount > 0 && (
            <div className={styles.sortBy}>
              <div
                className={`${styles.b} ${activeSort === 'best' ? styles.active : ''}`}
                onClick={() =>
                  handleSort(
                    {
                      field: ReviewSortField.RATING,
                      direction: OrderDirection.DESC,
                    },
                    'best'
                  )
                }
              >
                {t('top-ratings')}
              </div>
              <div
                className={`${styles.b} ${activeSort === 'worst' ? styles.active : ''}`}
                onClick={() =>
                  handleSort(
                    {
                      field: ReviewSortField.RATING,
                      direction: OrderDirection.ASC,
                    },
                    'worst'
                  )
                }
              >
                {t('poor-ratings')}
              </div>
              <div
                className={`${styles.b} ${activeSort === 'new' ? styles.active : ''}`}
                onClick={() =>
                  handleSort(
                    {
                      field: ReviewSortField.PUBLISHED_AT,
                      direction: OrderDirection.DESC,
                    },
                    'new'
                  )
                }
              >
                {t('new')}
              </div>
            </div>
          )}
        {showSkeletons ? (
          <div className={styles.testimonials}>
            {Array(2)
              .fill(null)
              .map((_, index) => (
                <div className={styles.testimonial} key={index}>
                  <div className={styles.user}>
                    <Skeleton
                      circle
                      width={48}
                      height={48}
                      className={styles.skeleton}
                    />
                    <div className={styles.userName}>
                      <Skeleton
                        width={150}
                        height={20}
                        className={styles.skeleton}
                      />
                      <Skeleton
                        width={80}
                        height={16}
                        className={styles.skeleton}
                      />
                    </div>
                  </div>
                  <div className={styles.text}>
                    {Array(3)
                      .fill(null)
                      .map((_, index) => (
                        <div className={styles.advantages} key={index}>
                          <Skeleton
                            width={80}
                            height={20}
                            className={styles.skeleton}
                          />
                          <Skeleton
                            width={150}
                            height={20}
                            className={styles.skeleton}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className={styles.testimonials}>
            {reviewsListData?.map(review => (
              <div className={styles.testimonial} key={review.id}>
                <div className={styles.user}>
                  {review?.user?.avatar?.url ? (
                    <Image
                      className={styles.avatarIcon}
                      alt="avatar-icon"
                      src={
                        review?.user?.avatar === null
                          ? DefaultImage
                          : review?.user?.avatar?.url
                      }
                      width={46}
                      height={36}
                    />
                  ) : (
                    <FaRegUser className={styles.placeholderIcon} />
                  )}
                  <div className={styles.userName}>
                    <div className={styles.parent}>
                      <div className={styles.div2}>
                        {review.user?.firstName || t('anonim')}{' '}
                        {review.user?.lastName || ''}
                      </div>
                      <div className={styles.starParent}>
                        <IconStar />
                        <div className={styles.div2}>{review.rating}</div>
                      </div>
                    </div>
                    <div className={styles.div4}>
                      {formatDate(review.publishedAt)}
                    </div>
                  </div>
                </div>
                <div className={styles.text}>
                  {review.advantages && (
                    <div className={styles.advantages}>
                      <div className={styles.div5}>{t('merit')}</div>
                      <div className={styles.rustore}>{review.advantages}</div>
                    </div>
                  )}
                  {review.disadvantages && (
                    <div className={styles.advantages}>
                      <div className={styles.div5}>{t('shortcoming')}</div>
                      <div className={styles.rustore}>
                        {review.disadvantages}
                      </div>
                    </div>
                  )}
                  {review.description && (
                    <div className={styles.advantages}>
                      <div className={styles.div5}>{t('comments')}</div>
                      <div className={styles.rustore}>{review.description}</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className={styles.pagination}>
          <Pagination
            ellipsis={true}
            maxButtons={3}
            boundaryLinks={true}
            size="sm"
            total={totalPages}
            limit={1}
            activePage={currentPage}
            onChangePage={handlePageChange}
          />
          {/* <div className={styles.buttonShowMore}>
						<div className={styles.master1}>
							<div className={styles.labelWrapper}>
								<div className={styles.label7}>Показать еще</div>
							</div>
						</div>
					</div> */}
        </div>
      </div>
      <UserReviewModal
        open={open}
        close={setOpen}
        productId={data?.product?.defaultVariant?.id}
        data={data}
      />
    </div>
  );
};

export default Rating;
